import { SENDA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { idiv } from '../math/fixed.ts';
import type { CartsRules, DrillsRules, StrutsRules } from '../types.ts';
import type { CartsLayout, DrillsLayout, StrutsLayout } from './ojos-layout.ts';

/**
 * The eyes' rules from their layouts (docs/sendas/artifice.md, chapter 4): the fight's builder (campaign/sendas.ts) and
 * the renderer's scenes build them the same way. SENDA.struts, carts and drills. La Barrena's life, the quarrymen's
 * picks and Dunna's bolt in hit points (seconds of D, worked out by the caller). `radius`: the board's, for cell ids.
 */

export const strutsRules = (L: StrutsLayout, radius: number, pickHit: number): StrutsRules => {
  const S = SENDA.struts;
  return {
    struts: L.struts.map((h) => cellId(radius, h.q, h.r)),
    doors: L.doors.map((p) => ({ ...p })),
    rim: L.rim.map((p) => ({ ...p })),
    creak: [...L.creak],
    caulk: S.caulk,
    light: S.light,
    lossBp: S.lossBp,
    sway: S.sway,
    swayGap: S.swayGap,
    houses: S.houses,
    reach: S.reach,
    golem: { ...S.golem },
    bruni: { pace: S.bruni.pace, at: { ...L.bruniAt } },
    foes: { ...S.foes },
    groan: { ...S.groan },
    pick: { every: S.pick.every, hit: pickHit, range: S.pick.range },
  };
};

export const cartsRules = (L: CartsLayout, radius: number, dunnaHit: number): CartsRules => {
  const C = SENDA.carts;
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  return {
    rails: L.rails.map((rl) => ({
      cells: rl.cells.map(id),
      path: rl.cells.map((h) => hexToWorld(h.q, h.r)),
      brake: id(rl.brake),
      gate: rl.gate.map(id).sort((a, b) => a - b),
      beetles: rl.beetles.map((p) => ({ ...p })),
    })),
    holes: L.holes.map((h) => ({ ...h })),
    way: L.way.map((p) => ({ ...p })),
    door: { ...L.door },
    brake: C.brake,
    reach: C.reach,
    pace: C.pace,
    again: C.again,
    shellBp: C.shellBp,
    foeBp: C.foeBp,
    heroBp: C.heroBp,
    doorR: C.door,
    beetle: { ...C.beetle },
    crack: { ...C.crack },
    foes: { ...C.foes },
    dunna: { every: C.dunna.every, hit: dunnaHit, range: C.dunna.range },
  };
};

export const drillsRules = (L: DrillsLayout, radius: number, bossHp: number, dunnaHit: number): DrillsRules => {
  const D = SENDA.drills;
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  const mid = (cells: { q: number; r: number }[]): { x: number; y: number } => {
    const ps = cells.map((h) => hexToWorld(h.q, h.r));
    const n = ps.length || 1;
    return { x: idiv(ps.reduce((a, p) => a + p.x, 0), n), y: idiv(ps.reduce((a, p) => a + p.y, 0), n) };
  };
  return {
    drills: L.drills.map((h, d) => ({
      cell: id(h),
      spot: id(L.spots[d] as { q: number; r: number }),
      at: hexToWorld(h.q, h.r),
      force: L.set.force[d] as number,
      wall: L.set.wall[d] as number,
      right0: L.set.right0[d] as boolean,
    })),
    levers: L.levers.map(id),
    walls: L.walls.map(mid),
    ice: L.ice.map((p) => ({ ...p })),
    mill: D.mill,
    clutch: D.clutch,
    lever: D.lever,
    reach: D.reach,
    cut: D.cut,
    jamBp: D.jam.heroBp,
    pulse: { ...D.ice },
    foes: { ...D.foes },
    boss: { ...D.boss, spin: { ...D.boss.spin }, hp: bossHp, at: { ...L.heap } },
    dunnaAt: { ...L.dunnaAt },
    dunna: { every: D.dunna.every, hit: dunnaHit, range: D.dunna.range },
  };
};
